import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { format, parse, isValid } from 'date-fns';
import { addPatient, updatePatient } from '../../services/patientService'; // Import updatePatient service

const AddPatientModal = ({ open, onClose, onSubmit, initialData = {}, showSnackbar }) => {
    const initialFormData = {
        id: initialData.id || '',  // Include ID for update
        name: '',
        lastname: '',
        id_number: '',
        phone: '',
        email: '',
        birthday: '',
        address: '',
    };

    const [formData, setFormData] = useState(initialFormData);
    const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state to prevent duplicate submissions

    useEffect(() => {
        if (open) {
            setFormData({
                id: initialData.id || '',
                name: initialData.name || '',
                lastname: initialData.lastname || '',
                id_number: initialData.id_number || '',
                phone: initialData.phone || '',
                email: initialData.email || '',
                birthday: initialData.birthday ? format(parse(initialData.birthday, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy') : '',
                address: initialData.address || '',
            });
        }
    }, [open, initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;
        setIsSubmitting(true);

        if (formData.birthday && !isValid(parse(formData.birthday, 'dd.MM.yyyy', new Date()))) {
            showSnackbar('Enter a valid birthday in DD.MM.YYYY format.', 'error');
            setIsSubmitting(false);
            return;
        }
        const formattedBirthday = formData.birthday
            ? format(parse(formData.birthday, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd')
            : '';

        const patientData = { ...formData, birthday: formattedBirthday };

        try {
            let response;
            if (initialData.id) {
                response = await updatePatient(patientData);
            } else {
                response = await addPatient(patientData);
            }

            if (response.success && response.data.status === "success") {
                showSnackbar(initialData.id ? 'Patient updated successfully.' : 'Patient added successfully.', 'success');
                onSubmit(response.data.patient || patientData); // Ensure we handle both add and update
                onClose(); // Close the modal
            } else if (response.data?.status === "exists") {
                showSnackbar('Patient already exists.', 'info');
            } else {
                showSnackbar('Error saving patient.', 'error');
            }
        } catch (error) {
            console.error('Error saving patient:', error);
            showSnackbar('An error occurred. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{initialData.name ? 'Edit Patient' : 'Add Patient'}</DialogTitle>
            <DialogContent>
                <div className="row">
                    <div className="col-12 col-lg-6">
                        <TextField
                            label="First Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </div>
                    <div className="col-12 col-lg-6">
                        <TextField
                            label="Last Name"
                            name="lastname"
                            value={formData.lastname}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </div>
                    <div className="col-12 col-lg-6">
                        <TextField
                            label="ID Number"
                            name="id_number"
                            value={formData.id_number}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </div>
                    <div className="col-12 col-lg-6">
                        <TextField
                            label="Phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </div>
                    <div className="col-12 col-lg-6">
                        <TextField
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </div>
                    <div className="col-12 col-lg-6">
                        <TextField
                            label="Birthday (dd.MM.yyyy)"
                            name="birthday"
                            placeholder="dd.MM.yyyy"
                            value={formData.birthday}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </div>
                    <div className="col-12 col-lg-6">
                        <TextField
                            label="Address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
                    {initialData.name ? 'Update' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddPatientModal;
